import {Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import Button from '@mui/material/Button';
import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    getEmailByUserIdGraphAPILambda,
    saveWithoutChangeLogLambda,
    sendEmailLambda,
    toErrorFlow,
} from '../../../utils/functionSignatures';
import {BrickContext, Config, ErrorFlow} from 'olympe';
import {PurchaseRequest} from '../../../models/PurchaseRequest';
import {getMailContentPRToApprove} from '../../../utils/email';
import {createPRJsonWithSubmissionDate} from '../../../utils/createPRJson';
import {ErrorPopup} from './ErrorPopup.jsx';
import {tagToDisplayER} from '../../../utils/tagTransforms';

/**
 * This component display a simple popup with an error message
 * @param {ApprovalNeededPopupNewSubmit.propTypes} props
 * @return {JSX.Element}
 */
export function ApprovalNeededPopupNewSubmit(props) {
    const numberOfReasons = props.reasonsForApproval.length;
    const multipleReasons = numberOfReasons > 1;

    const [open, setOpen] = useState(true);
    const [approversBeingFetched, setApproversBeingFetched] = useState(false);

    const [errorPopupOpen, setErrorPopupOpen] = useState(false);
    const [errorPopupMsg, setErrorPopupMsg] = useState([]);

    // Display the popup each time we receive a new error/success
    useEffect(() => {
        setOpen(true);
    }, [props.inputCtrlFlow]);

    const openErrorPopup = (msg) => {
        setApproversBeingFetched(false);
        setErrorPopupMsg([msg]);
        setErrorPopupOpen(true);
    };
    const handleCloseErrorPopup = () => {
        setErrorPopupOpen(false);
    };

    const handleCancel = () => {
        setOpen(false);
        props.setErrorFlow(ErrorFlow.create('Approval canceled', 2072));
    };

    const sendEmailAndSave = (purchaseRequest, emailRecipients, env) => {
        if (emailRecipients) {
            const EPRid = purchaseRequest.EPRId || tagToDisplayER(props.tag);
            const now = new Date();
            const readableDateOptions = {month: 'short', day: 'numeric', year: 'numeric'};
            const readableSubmissionDate = now.toLocaleDateString('en-US', readableDateOptions);
            const mailContent = getMailContentPRToApprove(multipleReasons, props.reasonsForApproval, props.tag, EPRid, purchaseRequest.requester, purchaseRequest.vendorCode, purchaseRequest.vendorName, purchaseRequest.projectTitle, readableSubmissionDate);
            const requesterLabel = purchaseRequest.requester?.label ?? '';
            const vendorNameValue = purchaseRequest.vendorName ?? '';
            const subject = `[Easy Request] ${env !== 'prod' ? (env === 'codev' ? '[DEV] ' : '[PRE-PROD] ') : ''}PR to check! ${EPRid}${requesterLabel ? ` | ${requesterLabel}` : ''}${vendorNameValue ? ` | ${vendorNameValue}` : ''}`;

            sendEmailLambda(props.$, props.sendEmail, emailRecipients, '', '', subject, mailContent, undefined)
                .then(() => {
                    setOpen(false);
                    saveWithoutChangeLogLambda(props.$, props.savePR, props.tag, createPRJsonWithSubmissionDate(purchaseRequest))
                        .then(() => {
                            setApproversBeingFetched(false);
                            props.forwardEvent();
                        })
                        .catch((reason) => {
                            openErrorPopup('Something went wrong while saving the PR.');
                        });
                })
                .catch((reason) => {
                    openErrorPopup(`Something went wrong while sending email notification to approvers. ${reason}`);
                });
        }
    };

    const handleCloseSB = (event, reason) => {
        // User canceled via outside click or Escape button
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            setOpen(false);
            return;
        }

        const purchaseRequest = new PurchaseRequest(props.pr);
        const env = Config.getParameter('ENV');

        // SB
        getEmailByUserIdGraphAPILambda(props.$, props.getUserIdGraphAPI, props.sbApprovers)
            .then((userId) => {
                const result = JSON.parse(userId[0]).responses.flatMap((row) => row.body.value).map((row) => ({
                    label: `${row['givenName']} ${row['surname']}`,
                    value: row['onPremisesSamAccountName'],
                    email: row['userPrincipalName'],
                }))

                if (env === 'preprod') {
                    result.push({
                        label: 'Amara Vani Adapa',
                        value: 'CAADAPAAM',
                        email: 'amaravani.adapa@ca.nestle.com',
                    });
                    result.push({
                        label: 'Kumaresan Balasubramanian',
                        value: 'AUKUMAREBA',
                        email: 'balasubramanian.kumaresan@aoa.nestle.com',
                    });
                }

                if (result.length === 0) {
                    throw ErrorFlow.create(`The following strategic buyer(s) is not in the database: ${props.sbApprovers}. Please contact your administrator`, 2071);
                }

                purchaseRequest.sbValidation.strategicBuyers = result;
                purchaseRequest.status = PurchaseRequest.statuses.pendingSB;

                sendEmailAndSave(purchaseRequest, props.sbApprovers.join(';'), env);
            })
            .catch((reason) => {
                openErrorPopup(`Something went wrong while fetching SB User IDs. ${toErrorFlow(reason).getMessage()}`);
            });
    };

    return (
        <>
            <Dialog
                onClose={handleCancel}
                open={open}
                aria-labelledby="approval-needed-popup"
            >
                <div style={{display: 'flex'}}>
                    <div style={{backgroundColor: '#F19002', width: '20%', height: 'auto', display: 'flex', alignItems: 'center', minWidth: '50px', justifyContent: 'center'}}>
                        <i className="material-icons" style={{color: 'white'}}>{'pending_actions'}</i>
                    </div>
                    <div>
                        <DialogTitle id="popup-title">{`Your request need an approval.`}</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {`This request will be sent to a Strategic Buyer, pending an approval. The validation of your request is needed because of the following reason${multipleReasons ? 's' : ''}`}
                            </DialogContentText>
                            <ul>
                                {props.reasonsForApproval.map((msg, idx) => (
                                    <li key={msg + idx}>
                                        <DialogContentText id="error-popup-description" >
                                            {msg}
                                        </DialogContentText>
                                    </li>
                                ))}
                            </ul>
                            <DialogContentText>
                                {`An email will be sent to the SB indicating he need to check your request.`}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions style={{alignItems: 'baseline'}}>
                            {approversBeingFetched && <span className='popup-action-label'>Searching SBs for your company code...</span>}
                            <Button onClick={handleCancel}>
                                Cancel
                            </Button>
                            <LoadingButton
                                data-cy={'Approval_needed_ok_btn'}
                                onClick={(event, reason) => handleCloseSB(event, reason)}
                                style={{
                                    paddingRight: approversBeingFetched ? '40px' : '15px',
                                }}
                                loading={approversBeingFetched}
                                loadingPosition={'end'}
                                // Use empty div to prevent getting warnings.
                                endIcon={<div />}
                                disabled={approversBeingFetched}
                            >
                                Ok
                            </LoadingButton>
                        </DialogActions>
                    </div>
                </div>
            </Dialog>
            <ErrorPopup onClose={handleCloseErrorPopup} open={errorPopupOpen} errorMessages={errorPopupMsg}/>
        </>
    );
}

ApprovalNeededPopupNewSubmit.propTypes = {
    $: PropTypes.instanceOf(BrickContext).isRequired,
    pr: PropTypes.string.isRequired,
    tag: PropTypes.string.isRequired,
    inputCtrlFlow: PropTypes.number,
    forwardEvent: PropTypes.func.isRequired,
    setErrorFlow: PropTypes.func.isRequired,
    getUserIdGraphAPI: PropTypes.object.isRequired,
    sendEmail: PropTypes.object.isRequired,
    savePR: PropTypes.object.isRequired,
    sbApprovers: PropTypes.arrayOf(PropTypes.string).isRequired,
    reasonsForApproval: PropTypes.arrayOf(PropTypes.string).isRequired,
};
